/**
 * DataOriginExplainer — progressive-disclosure explainer for DATA.ORIGIN (D1).
 *
 * DATA.ORIGIN is the fibration side of the DATA.LINEAGE dual pair. It
 * neutralizes the vulnerability class "provenance is misrepresented" by
 * making the forged-origin morphism structurally inexpressible: every
 * morphism in the total category carries a cartesian lift witnessing the
 * provenance ordering, so there is no closed term that names data of one
 * origin while claiming another.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { DATA_ORIGIN_META } from "@csk/core";
import { THEOREM_COLOR, withAlpha } from "../../design-system/tokens.js";
import { MathInline, tex } from "../../components/math/index.js";
import {
  ExplainerCard,
  FibrationDiagram,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";
import { TheoremMetaStrip } from "./TheoremMetaStrip.js";
import { TIER_ONE_IMMUNITY_LINK } from "./tierOneCrossLinks.js";

const ACCENT = THEOREM_COLOR["DATA.ORIGIN"];

const IDENTITY: ExplainerIdentity = {
  id: "explainer-data-origin",
  kind: "SSM · Neutralization Theorem (S1)",
  title: "DATA.ORIGIN",
  subtitle: "D1 · Fibration · IMMUNE · G2 Knowledge",
  accent: ACCENT,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  { label: "Fibration (structure)", href: "#explainer-fibration", accent: ACCENT },
  {
    label: "DATA.REACH (dual)",
    href: "#explainer-data-reach",
    accent: THEOREM_COLOR["DATA.REACH"],
  },
  { label: "SEPARATE (G2 sibling)", href: "#explainer-separate", accent: THEOREM_COLOR.SEPARATE },
  TIER_ONE_IMMUNITY_LINK,
];

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      <strong>DATA.ORIGIN</strong> says: every value carries its provenance with it, by
      construction. A value cannot claim an origin it does not have — there is no way to write that
      lie.
    </p>
  ),
  intuition: (
    <>
      <p>
        Most systems track provenance with a side-table: a field that <em>says</em> where data came
        from. The classic failure is that the field and the data drift apart — an attacker relabels
        untrusted bytes as "trusted upstream" and the side-table cannot tell.
      </p>
      <p>
        CSK organizes data as a fibration over a base of provenance levels. Every morphism comes
        with a cartesian lift sitting above the base morphism that re-indexes it. To move a value
        between origins you must exhibit that lift; relabeling without it is not "rejected" — it is
        unconstructable in the source.
      </p>
    </>
  ),
  formal: (
    <>
      <p>
        Let <MathInline>{tex`\pi : E \to B`}</MathInline> be the provenance fibration, with{" "}
        <MathInline>{tex`B`}</MathInline> the poset of origin levels and{" "}
        <MathInline>{tex`E`}</MathInline> the total category of values-with-provenance. For a base
        morphism <MathInline>{tex`f : A \to B`}</MathInline> and an object{" "}
        <MathInline>{tex`e`}</MathInline> over <MathInline>{tex`B`}</MathInline>, the cartesian lift{" "}
        <MathInline>{tex`\tilde{f} : f^{*}(e) \to e`}</MathInline> is the unique mediating map any
        other lift factors through.
      </p>
      <p style={{ marginTop: "0.5rem" }}>
        Misrepresentation would be a morphism <MathInline>{tex`e_{1} \to e_{2}`}</MathInline> whose
        image under <MathInline>{tex`\pi`}</MathInline> is not the order witness{" "}
        <MathInline>{tex`\mathrm{origin}(e_{1}) \le \mathrm{origin}(e_{2})`}</MathInline>. No such
        morphism exists, because every arrow in <MathInline>{tex`E`}</MathInline> is a cartesian
        lift of its projection. <code>DATA.ORIGIN</code> is a projection of the Categorical Immunity
        Theorem, not an independent argument.
      </p>
    </>
  ),
  relevance: (
    <>
      <p>
        <strong>Vulnerability class neutralized:</strong> provenance forgery — untrusted data
        presented as trusted upstream (CWE-345, CWE-494, CWE-829). CSK realizes this by{" "}
        <em>structural inexpressibility</em>: the forged-origin morphism is not a closed term in the
        kernel category.
      </p>
      <p>
        <strong>Theorem-collision check.</strong> If you reconstruct provenance by parsing a raw
        header into the carrier, you have left <code>E</code> and are working in an uncertified
        carrier. DATA.ORIGIN is then vacuous there — the witness has gone away, not been bypassed.
      </p>
      <p>
        <strong>Seam type</strong> (per <code>theory/structural/SEAM-THEORY.md</code>):{" "}
        {DATA_ORIGIN_META.seamType}. <strong>Bridge</strong>: declassification certificate
        (governance artifact).
      </p>
    </>
  ),
  references: [
    {
      label: "DATA.ORIGIN theorem card",
      path: "docs/reference/theorem-cards/DATA-ORIGIN.md",
    },
    {
      label: "Kernel module (main theorem DATA-ORIGIN-neutralized)",
      path: `agda/${DATA_ORIGIN_META.agdaSource}`,
      note: `DATA_ORIGIN_META.agdaSource is "${DATA_ORIGIN_META.agdaSource}"; the repo path is prefixed with "agda/". Main theorem at lines 111–135.`,
    },
    {
      label: "Theorem Canon (§4.1 IMMUNE mode details)",
      path: "docs/reference/Theorem-Canon.md",
    },
  ],
};

const DIAGRAM = (
  <>
    <TheoremMetaStrip
      theoremName="DATA.ORIGIN"
      code="D1"
      structure="Fibration"
      mode="immune"
      generator="G2"
      seamType={DATA_ORIGIN_META.seamType}
      bridgeArtifact="declassification certificate"
      theoremColor={ACCENT}
      washColor={withAlpha(ACCENT, "1A")}
    />
    <div style={{ marginTop: "0.5rem" }}>
      <FibrationDiagram
        accent={ACCENT}
        ariaLabel="Provenance fibration π : E → B with cartesian lift f̃ above the base re-indexing f"
      />
    </div>
  </>
);

export function DataOriginExplainer(): React.JSX.Element {
  return (
    <ExplainerCard identity={IDENTITY} tiers={TIERS} diagram={DIAGRAM} crossLinks={CROSS_LINKS} />
  );
}
